/**
 * Mana Locals Admin — M01 tenant INVITATION boundary schemas.
 *
 * CONTRACT SOURCE: `contracts/openapi/foundation.v1.yaml` (API repository),
 * contract register v1.4. These shapes mirror the API's invitation slice and are
 * validated on every response.
 *
 * THE TOKEN IS WRITE-ONLY
 * -----------------------
 * `token` appears in the CREATE response and NOWHERE else. It is `null` on a
 * replay. It is never present in the list response, and never present in an
 * error. The UI surfaces it exactly once, on the create result, and never stores
 * it.
 *
 * WHY AN INVITATION EXISTS
 * ------------------------
 * An invitation confers a role on an email that has no account yet. It is the
 * path for "assign an administrator before they have signed up". Once the
 * invitee accepts, the invitation becomes a membership.
 */
import { z } from 'zod';
import { IsoDateTimeSchema, UuidSchema } from '@/api/schemas/common';

/** The invitation's closed status set, mirroring `ck_tenant_invitation_status`. */
export const InvitationStatusSchema = z.enum(['pending', 'accepted', 'revoked', 'expired']);
export type InvitationStatus = z.infer<typeof InvitationStatusSchema>;

/** The role an invitation confers, mirroring `ck_tenant_invitation_role`. */
export const InvitationRoleSchema = z.enum(['owner', 'staff', 'viewer']);
export type InvitationRole = z.infer<typeof InvitationRoleSchema>;

/** The maximum invitation lifetime, in days. Mirrors `MAX_INVITATION_DAYS`. */
export const INVITATION_MAX_DAYS = 90;

export const CreateInvitationRequestSchema = z
  .object({
    email: z.string().email().max(320),
    role: InvitationRoleSchema,
    expiresInDays: z.number().int().min(1).max(INVITATION_MAX_DAYS).optional(),
  })
  .strict();
export type CreateInvitationRequest = z.infer<typeof CreateInvitationRequestSchema>;

/** The invitation document, WITHOUT the token. */
export const InvitationSchema = z
  .object({
    id: UuidSchema,
    tenantId: UuidSchema,
    email: z.string().email().max(320),
    role: InvitationRoleSchema,
    status: InvitationStatusSchema,
    expiresAt: IsoDateTimeSchema,
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
  })
  .strict();
export type Invitation = z.infer<typeof InvitationSchema>;

/**
 * The CREATE response. `token` is present ONLY on a fresh create and is `null`
 * on a replay. It is the one and only time the raw token is ever readable.
 */
export const InvitationCreatedSchema = InvitationSchema.extend({
  token: z.string().min(1).nullable(),
}).strict();
export type InvitationCreated = z.infer<typeof InvitationCreatedSchema>;

const InvitationMetaSchema = z.object({ requestId: z.string().max(128) }).strict();

export const InvitationCreatedResponseSchema = z
  .object({ data: InvitationCreatedSchema, meta: InvitationMetaSchema })
  .strict();
export type InvitationCreatedResponse = z.infer<typeof InvitationCreatedResponseSchema>;

export const InvitationListResponseSchema = z
  .object({ data: z.array(InvitationSchema), meta: InvitationMetaSchema })
  .strict();
export type InvitationListResponse = z.infer<typeof InvitationListResponseSchema>;

export const InvitationSuccessResponseSchema = z
  .object({ data: InvitationSchema, meta: InvitationMetaSchema })
  .strict();
export type InvitationSuccessResponse = z.infer<typeof InvitationSuccessResponseSchema>;

/** Safe, non-disclosing error codes for this slice. */
export const InvitationErrorCodeSchema = z.enum([
  'INVALID_INVITATION_INPUT',
  'TENANT_NOT_FOUND',
  'INVITATION_ALREADY_PENDING',
  'INVITATION_NOT_FOUND',
  'IDEMPOTENCY_KEY_REUSED',
  'IDEMPOTENCY_KEY_REQUIRED',
  'UNAUTHENTICATED',
  'FORBIDDEN',
  'SERVICE_UNAVAILABLE',
]);
export type InvitationErrorCode = z.infer<typeof InvitationErrorCodeSchema>;
